<template>
  <div class="organization-user-selector">
    <UiFormLayout>
      <UiFormItem :label="t('common.userSelect.employee')" :minWidth="30">
        <div class="org-user-search-row">
          <UiInput :placeholder="t('common.userSelect.employeeNamePlaceholder')" v-model="employeeName" class="org-user-search-input" @keyup.enter="searchUser" />
          <UiButton variant="secondary" icon-only class="org-user-search-btn" @click="searchUser">
            <i class="icon icon-md icon-search icon-white"></i>
          </UiButton>
          <UiSwitch v-model="showUserId" :label="t('common.userSelect.showUserId')" class="org-user-id-switch" />
        </div>
      </UiFormItem>
    </UiFormLayout>

    <!-- 3컬럼 레이아웃 -->
    <div class="selector-columns">
      <!-- 1. 조직 트리 -->
      <div class="column organization-tree-column">
        <div class="column-header">
          <h3 class="column-title">{{ t('common.userSelect.organization') }}</h3>
          <div class="tree-actions">
            <button class="tree-action-btn" @click="expandAllOrgs">
              <i class="icon icon-sm icon-plus"></i>
              {{ t('common.userSelect.expandAll') }}
            </button>
            <button class="tree-action-btn" @click="collapseAllOrgs">
              <i class="icon icon-sm icon-minus"></i>
              {{ t('common.userSelect.collapseAll') }}
            </button>
          </div>
        </div>

        <!-- 조직 트리 구조 -->
        <!-- 재귀 OrganizationTreeItem 컴포넌트 적용 -->
        <div class="organization-tree">
          <OrganizationTreeItem
            v-for="org in deptList.filter(d => !d.upDeptId)"
            :key="getFullDeptId(org) || org.deptId"
            :item="org"
            :list="deptList"
            :selected-id="selectedOrgId"
            :expanded-ids="expandedOrgs"
            @toggle="toggleOrg"
            @select="selectOrganization"
          />
        </div>
      </div>

      <!-- 2. 소속 직원 -->
      <div class="column department-users-column">
        <div class="column-header">
          <h3 class="column-title">{{ t('common.userSelect.departmentUsers') }}</h3>
        </div>
        <Empty
          v-if="departmentUsers.length === 0"
          iconType="search"
          :title="t('common.userSelect.noResults')"
          :description="t('common.userSelect.tryDifferentKeyword')"
          style="flex: 1"
        />
        <div v-else class="user-list">
          <div
            class="user-item"
            v-for="user in departmentUsers"
            :key="user.userId"
            :class="{ selected: selectedFromDept.has(user.userId) }"
            @click="toggleDeptUser(user.userId)"
            @dblclick="moveSelectedToRight(user.userId)"
          >
            <div class="user-info">
              <span class="user-name">{{ user.userNm }}</span>
              <span v-if="showUserId && user.userId" class="user-id">{{ user.userId }}</span>
              <span class="user-rank">[{{ user.jikgubNm }}]</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. 이동 버튼 -->
      <div class="transfer-buttons-column">
        <div class="transfer-buttons">
          <button class="transfer-btn transfer-right" @click="moveSelectedToRight()">
            <i class="icon icon-md icon-arrow-right"></i>
          </button>
          <button class="transfer-btn transfer-left" @click="removeSelectedUsers()">
            <i class="icon icon-md icon-arrow-left"></i>
          </button>
        </div>
      </div>

      <!-- 4. 선택된 직원 -->
      <div class="column selected-users-column">
        <div class="column-header">
          <h3 class="column-title">{{ t('common.userSelect.selectedUsers') }}</h3>
        </div>
        <div class="user-list">
          <div
            class="user-item"
            v-for="user in selectedUsers"
            :key="user.userId"
            :class="{ selected: selectedFromSelected.has(user.userId) }"
            @click="toggleSelectedUser(user.userId)"
            @dblclick="removeSelectedUsers(user.userId)"
          >
            <div class="user-info">
              <span class="user-name">{{ user.userNm }}</span>
              <span v-if="showUserId && user.userId" class="user-id">{{ user.userId }}</span>
              <span v-if="user.jikgubNm" class="user-rank">[{{ user.jikgubNm }}]</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useUserSelectorApi } from '~/composables/useUserSelectorApi.js';
import { useUserSelectorStore } from '~/composables/useUserSelectorStore.js';
import OrganizationTreeItem from '~/components/user/OrganizationTreeItem.vue';

const { expandedOrgs, deptList, selectedOrgId, employeeName, orgEmployeesData, selectedFromDept, selectedFromSelected, isSearch, selectedUsers, showUserId } =
  useUserSelectorStore();
const { fetchDeptList, fetchUserList, fetchDeptInsaList, fetchUserInsaList } = useUserSelectorApi();
const { userInfo } = useUserStore();
const { t } = useI18n();

const props = defineProps({
  findYear: {
    type: String,
    default: '',
  },
  evalCycleCd: {
    type: String,
    default: '',
  },
  // 선택 대상에서 제외할 사용자 ID 목록 (예: 배포 대상에서 본인 제외)
  excludeUserIds: {
    type: Array,
    default: () => [],
  },
});

// findYear·evalCycleCd가 모두 있으면 인사정보 기준으로 조회
const shouldUseInsaList = () => !!props.findYear && !!props.evalCycleCd;

// fullDeptId(전체 경로) 필드를 방어적으로 읽는다. (트리 key 중복 방지용)
const getFullDeptId = dept => String(dept?.fullDeptId ?? dept?.full_dept_id ?? dept?.fullDeptPath ?? '');

onMounted(async () => {
  try {
    const res = shouldUseInsaList()
      ? await fetchDeptInsaList({ findYear: props.findYear, evalCycleCd: props.evalCycleCd })
      : await fetchDeptList({ findCompId: userInfo.value.compId });
    deptList.value = res.list ?? [];
    expandedOrgs.value = deptList.value.map(d => d.deptId);
  } catch (err) {
    console.error('담당자 검색 실패', err);
  }
});

// 담당자 선택 팝업 담담자 검색
//TODO  findYear 추후 변경
const searchUser = async () => {
  isSearch.value = true;

  try {
    const result = shouldUseInsaList()
      ? await fetchUserInsaList({
          findYear: props.findYear,
          deptId: selectedOrgId.value || '',
          userNm: employeeName.value || '',
          evalCycleCd: props.evalCycleCd,
          userIdYn: showUserId.value ? 'Y' : 'N',
        })
      : await fetchUserList({
          compId: userInfo.value.compId,
          userNm: employeeName.value,
          findYear: '2024',
        });
    orgEmployeesData.value = result.list;
  } catch (err) {
    console.error('담당자 검색 실패', err);
  }
};

// 선택 불가 사용자 (제외 대상 or 이미 선택된 직원)
const isHiddenUser = user =>
  props.excludeUserIds.includes(user.userId) || !!selectedUsers.value.find(selected => selected.userId === user.userId);

// 현재 조직의 직원 목록 (선택된 직원·제외 대상에 없는 것만)
const departmentUsers = computed(() => {
  // 검색할 때
  if (isSearch.value) {
    return orgEmployeesData.value.filter(user => !isHiddenUser(user));
  }

  // 조직 선택 했을 때
  if (!selectedOrgId.value) return [];
  const orgUsers = orgEmployeesData.value.filter(user => user.deptId === selectedOrgId.value);
  return orgUsers.filter(user => !isHiddenUser(user));
});

// 소속 직원 선택/해제
const toggleDeptUser = userId => {
  if (selectedFromDept.value.has(userId)) {
    selectedFromDept.value.delete(userId);
  } else {
    selectedFromDept.value.add(userId);
  }
};

// 선택 직원 선택/해제
const toggleSelectedUser = userId => {
  if (selectedFromSelected.value.has(userId)) {
    selectedFromSelected.value.delete(userId);
  } else {
    selectedFromSelected.value.add(userId);
  }
};

// 오른쪽으로 이동 (소속 직원 → 선택 직원)
const moveSelectedToRight = userId => {
  let toMove;
  if (userId) {
    toMove = departmentUsers.value.filter(user => userId === user.userId);
  } else {
    toMove = departmentUsers.value.filter(user => selectedFromDept.value.has(user.userId));
  }
  // 🔥 새 배열 생성해서 전달
  const newSelectedUsers = [...selectedUsers.value];

  toMove.forEach(user => {
    newSelectedUsers.push({ ...user });
    selectedFromDept.value.delete(user.userId);
  });

  // 🔥 부모로 데이터 전달
  selectedUsers.value = newSelectedUsers;
};

// 왼쪽으로 이동 (선택 직원 → 제거)
const removeSelectedUsers = userId => {
  let newSelectedUsers;
  if (userId) {
    newSelectedUsers = selectedUsers.value.filter(user => user.userId !== userId);
  } else {
    newSelectedUsers = selectedUsers.value.filter(user => !selectedFromSelected.value.has(user.userId));
  }

  selectedFromSelected.value.clear();

  // 🔥 부모로 데이터 전달
  selectedUsers.value = newSelectedUsers;
};

// 조직 선택
// TODO findYear 추후 변경
const selectOrganization = async orgId => {
  // 검색 종료
  isSearch.value = false;

  try {
    selectedOrgId.value = orgId;
    // 부서에 따른 사용자 목록 조회
    const res = shouldUseInsaList()
      ? await fetchUserInsaList({
          findYear: props.findYear,
          deptId: orgId,
          userNm: '',
          evalCycleCd: props.evalCycleCd,
          userIdYn: showUserId.value ? 'Y' : 'N',
        })
      : await fetchUserList({ compId: 'isp', deptId: orgId, findYear: '2024' });
    orgEmployeesData.value = res.list;

    selectedFromDept.value.clear(); // 조직 변경시 선택 상태 초기화
  } catch (err) {
    console.error('부서에 따른 사용자 목록 조회 실패', err);
  }
};

// 조직 토글 (펼치기/접기) - 꺽새 클릭 시 트리 열고 닫기만 하고 API는 호출하지 않는다.
const toggleOrg = orgId => {
  const index = expandedOrgs.value.indexOf(orgId);
  if (index === -1) {
    expandedOrgs.value.push(orgId);
  } else {
    expandedOrgs.value.splice(index, 1);
  }
};

// 모든 조직 열기
const expandAllOrgs = () => {
  expandedOrgs.value = deptList.value.map(d => d.deptId);
};

// 모든 조직 접기
const collapseAllOrgs = () => {
  expandedOrgs.value = [];
};

defineExpose({ selectedUsers });
</script>

<style lang="scss" scoped>
.organization-user-selector {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 600px;
  border-radius: 4px;
  overflow: hidden;
}

.org-user-search-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.org-user-search-input {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 180px;
}

.org-user-id-switch {
  flex: 0 0 auto;
  white-space: nowrap;
}

.org-user-search-btn {
  flex: 0 0 auto;
}

.selector-columns {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.column {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: auto;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.column-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f5f5f5;
}

.column-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.tree-actions {
  display: flex;
  gap: 8px;
}

.tree-action-btn {
  display: flex;
  align-items: center;
  padding: 8\4px;
  font-size: 12px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }

  .icon {
    margin-right: 4px;
  }
}

.organization-tree {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.org-item-wrapper {
  margin-bottom: 4px;
}

.org-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: $font-size-md;

  .org-name {
    font-weight: 500;
  }

  &:hover {
    background-color: #f0f0f0;
  }

  &.hasChild {
    font-weight: 500;
  }

  &.expanded {
    background-color: rgba(#00aaff, 0.1); // 연한 파란색 (확장됨)
  }

  &.selected {
    background-color: rgba(#ff6b35, 0.15); // 연한 주황색 (선택됨)
    border: 1px solid #ff6b35;
  }
}

.org-toggle-icon {
  margin-right: 8px;
  color: #666;
}

.sub-organizations {
  margin-left: 24px;
  margin-top: 4px;

  .org-item:not(.hasChild) {
    &::before {
      content: '-';
      display: inline-block;
      margin-right: 4px;
      color: #666;
    }
  }
}

.user-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 4px;
  font-size: $font-size-md;
  cursor: pointer;
  border: 1px solid transparent; // 기본 투명 테두리

  &:hover {
    background-color: #f0f0f0;
  }

  &.selected {
    background-color: rgba(#28a745, 0.1); // 연한 초록색 (선택된 직원)
    border: 1px solid #28a745;
    color: #155724;
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-name {
  font-weight: 500;
}

.user-id {
  font-size: 12px;
  color: #888;
  margin-left: 4px;
}

.user-rank {
  font-size: 12px;
  color: #666;
}

/* 이동 버튼 영역 스타일 */
.transfer-buttons-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 60px;
}

.transfer-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.transfer-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: white;
  cursor: pointer;
  font-size: 20px;

  &:hover {
    background-color: #f0f0f0;
  }

  &.transfer-right {
    color: #0066cc;
  }

  &.transfer-left {
    color: #0066cc;
  }
}
</style>
